// ============================================================
// AnnouncementModal.js
// The login popup for announcements this user has never dismissed.
//
// WHY A POPUP AT ALL: the "What's New" card on the Dashboard is permanently
// visible, and anything permanently visible stops being seen by the person who
// opens that screen every morning. The modal is the one moment the message is
// unavoidable. It is shown ONCE per person per announcement and then never
// again — the card keeps it for reference afterwards.
//
// SEVERAL UNSEEN: shown ONE AT A TIME, oldest first, with an "n of m" counter.
// Combining them into one scroll would let a long backlog be dismissed with a
// single click without any of it being read, and would make "which of these
// have I acknowledged" unanswerable. One at a time means each id is dismissed
// only when that announcement has actually been in front of the reader.
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { relativeTime } from '../utils/announcements';

const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#9AA8BC';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';

/**
 * Two modes, ONE component, so an announcement looks identical however it was
 * opened.
 *
 *   login popup  — `items` (unseen, oldest first) + `onDismiss`. Advancing marks
 *                  each one seen. No Escape, no backdrop click: this is the one
 *                  moment the message is unavoidable, and a stray click on the
 *                  way to something else must not burn it.
 *
 *   readOnly     — a single announcement opened by hand from the What's New
 *                  card. NOTHING is marked seen. Reading an old announcement
 *                  must not silently dismiss a NEW one the reader has not been
 *                  shown yet, so this mode never calls onDismiss at all.
 *                  Closes on the button, on Escape and on a backdrop click,
 *                  because here those are just ways to stop reading.
 */
export default function AnnouncementModal({ items, onDismiss, onClose, readOnly = false }) {
  const [index, setIndex] = useState(0);
  const list = items || [];
  const current = list[index];
  const panelRef = useRef(null);

  // Escape closes, but only in read-only mode — see the note above.
  useEffect(() => {
    if (!readOnly) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [readOnly, onClose]);

  if (!current) return null;

  const advance = () => {
    if (readOnly) { onClose(); return; }
    // Dismiss THIS one only. If the write fails the announcement simply shows
    // again next login, which is the right way for this to fail.
    onDismiss(current.id);
    if (index + 1 < list.length) setIndex(index + 1);
    else onClose();
  };

  // Backdrop click, read-only only. Guarded on the target so a drag that starts
  // inside the panel and ends on the backdrop does not close it.
  const onBackdropMouseDown = (e) => {
    if (!readOnly) return;
    if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
  };

  const isLast = index + 1 >= list.length;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: 'rgba(3, 4, 94, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onMouseDown={onBackdropMouseDown}
    >
      <div ref={panelRef} style={{
        background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 20,
        boxShadow: '0 24px 60px rgba(3, 4, 94, 0.25)',
        width: '100%', maxWidth: 520, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${C_BORDER}`, background: C_TINT,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{
            color: C_ACCENT, fontSize: 11, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: 1.5,
          }}>
            📣 What&apos;s New
          </span>
          {!readOnly && list.length > 1 && (
            <span style={{ color: C_TEXT_2, fontSize: 12, fontWeight: 600 }}>
              {index + 1} of {list.length}
            </span>
          )}
        </div>

        <div style={{ padding: 24, overflowY: 'auto' }}>
          <h2 style={{ color: C_TEXT, fontSize: 18, fontWeight: 800, margin: '0 0 6px 0' }}>
            {current.title}
          </h2>
          <p style={{ color: C_TEXT_3, fontSize: 12, margin: '0 0 14px 0' }}>
            {current.author}{current.author ? ' · ' : ''}{relativeTime(current.createdAt)}
          </p>
          {/* preserve the author's line breaks without allowing markup */}
          <p style={{ color: C_TEXT_2, fontSize: 14, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {current.body}
          </p>
        </div>

        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${C_BORDER}`,
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            onClick={advance}
            style={{
              background: C_ACCENT, border: 'none', color: '#FFFFFF',
              padding: '10px 22px', borderRadius: 10,
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {readOnly ? 'Close' : isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
